/* ============================================================
   AURORE JEWELRY — Product Detail Page JavaScript
   File: assets/aurore-pdp.js

   Handles:
   1.  Gallery thumbnails / slide switching
   2.  Variant selectors (swatches, size buttons)
   3.  Metal-label live update
   4.  Gemstone custom dropdown
   5.  Hidden input sync → Shopify variant matching via Section Rendering API
   6.  Price + ATC button update on variant change
   7.  Accordion open / close (single-open policy)
   8.  SIZE GUIDE modal (open, close, tab switching)
   9.  CART DRAWER (open, close, add, qty, remove, upsell)
   10. Shipping progress bar
   11. Toast notifications
   12. Sticky ATC show/hide via IntersectionObserver
   13. Recently-viewed (localStorage)
   14. Escape-key + body-scroll-lock
   ============================================================ */

(function () {
  'use strict';

  /* ─────────────────────────────────────────
     CONFIG
  ───────────────────────────────────────── */
  var FREE_SHIPPING_THRESHOLD = 150; // dollars

  /* ─────────────────────────────────────────
     DOM HELPERS
  ───────────────────────────────────────── */
  function qs(sel, ctx)    { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx)   { return Array.from((ctx || document).querySelectorAll(sel)); }
  function on(el, ev, fn)  { if (el) el.addEventListener(ev, fn); }

  /* ─────────────────────────────────────────
     1. GALLERY
  ───────────────────────────────────────── */
  function initGallery() {
    var section = qs('.aurore-pdp');
    if (!section) return;

    var galleryMain = qs('.aurore-gallery-main', section);
    var slides  = qsa('.aurore-gallery-slide', section);
    var thumbs  = qsa('.aurore-gallery-thumb', section);
    var currentIndex = slides.findIndex(function(slide) {
      return slide.classList.contains('active');
    });
    var touchStartX = 0;
    var touchStartY = 0;
    var touchDeltaX = 0;
    var touchDeltaY = 0;
    var isHorizontalSwipe = false;
    var swipeStarted = false;

    if (currentIndex < 0) currentIndex = 0;

    function showSlide(idx) {
      if (!slides.length) return;
      if (idx < 0) idx = slides.length - 1;
      if (idx >= slides.length) idx = 0;
      currentIndex = idx;

      slides.forEach(function (s, i) {
        s.classList.toggle('active', i === idx);
        if (i !== idx) {
          qsa('video', s).forEach(function (video) { video.pause(); });
          qsa('iframe', s).forEach(function (frame) {
            if (frame.dataset.wasActive === 'true') frame.src = frame.src;
            frame.dataset.wasActive = 'false';
          });
        } else {
          qsa('iframe', s).forEach(function (frame) { frame.dataset.wasActive = 'true'; });
        }
      });
      thumbs.forEach(function (t, i) {
        t.classList.toggle('active', i === idx);
        t.setAttribute('aria-pressed', i === idx ? 'true' : 'false');
      });

      if (thumbs[idx] && window.matchMedia('(max-width: 899px)').matches) {
        thumbs[idx].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }

    thumbs.forEach(function (thumb, i) {
      on(thumb, 'click', function () { showSlide(i); });
    });

    if (galleryMain && slides.length > 1) {
      on(galleryMain, 'touchstart', function(event) {
        if (event.target.closest('video, iframe, model-viewer')) return;
        if (!window.matchMedia('(max-width: 899px)').matches || !event.touches.length) return;
        if (event.touches.length > 1) {
          swipeStarted = false;
          return;
        }
        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
        touchDeltaX = 0;
        touchDeltaY = 0;
        isHorizontalSwipe = false;
        swipeStarted = true;
      }, { passive: true });

      on(galleryMain, 'touchmove', function(event) {
        if (!swipeStarted || !event.touches.length) return;
        if (event.touches.length > 1) {
          swipeStarted = false;
          isHorizontalSwipe = false;
          return;
        }
        touchDeltaX = event.touches[0].clientX - touchStartX;
        touchDeltaY = event.touches[0].clientY - touchStartY;
        isHorizontalSwipe = Math.abs(touchDeltaX) > Math.abs(touchDeltaY) && Math.abs(touchDeltaX) > 18;
      }, { passive: true });

      on(galleryMain, 'touchend', function() {
        if (!swipeStarted) return;
        swipeStarted = false;
        if (!isHorizontalSwipe || Math.abs(touchDeltaX) < 44) return;

        if (touchDeltaX < 0) {
          showSlide(currentIndex + 1);
        } else {
          showSlide(currentIndex - 1);
        }
      });
    }
  }

  function getOrdinalSuffix(day) {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  function formatShipByDate(date) {
    var months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    var day = date.getDate();
    return months[date.getMonth()] + ' ' + day + getOrdinalSuffix(day);
  }

  function initEstimatedDelivery() {
    var delivery = qs('.aurore-delivery[data-estimated-days]');
    if (!delivery) return;

    var title = qs('[data-delivery-title]', delivery);
    var days = parseInt(delivery.dataset.estimatedDays, 10);
    if (!title || !Number.isFinite(days) || days < 0) return;

    var now = new Date();
    var shipDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (now.getHours() >= 14) {
      shipDate.setDate(shipDate.getDate() + 1);
    }
    shipDate.setDate(shipDate.getDate() + days);

    var shipByDate = formatShipByDate(shipDate);
    title.textContent = 'Order by 2 PM - Ships by ' + shipByDate;

    var shipsByProperty = qs('[data-ships-by-property]');
    if (shipsByProperty) {
      shipsByProperty.value = shipByDate;
    }
  }

  function initGiftNote() {
    var giftNote = qs('[data-gift-note]');
    if (!giftNote) return;

    var toggle = qs('[data-gift-note-toggle]', giftNote);
    var input = qs('[data-gift-note-input]', giftNote);
    if (!toggle || !input) return;

    function syncGiftNote() {
      var isOpen = toggle.checked;
      giftNote.classList.toggle('is-open', isOpen);
      input.disabled = !isOpen;
      if (!isOpen) input.value = '';
    }

    on(toggle, 'change', syncGiftNote);
    syncGiftNote();
  }

  /* ─────────────────────────────────────────
     2–6. VARIANT SELECTORS + SHOPIFY SYNC
  ───────────────────────────────────────── */
  function initVariants() {
    var section   = qs('.aurore-pdp');
    if (!section) return;

    var variantIdInput = qs('.aurore-variant-id-input', section);  // hidden input for variant ID
    var atcBtn        = qs('.aurore-atc-btn', section);
    var priceEl       = qs('#aurore-price', section);
    var discountBlock = qs('#aurore-discount-block', section);
    var wasEl         = qs('#aurore-was', section);
    var saveBadgeEl   = qs('#aurore-save-badge', section);
    var compareEl     = qs('#aurore-compare', section);
    var compareBlock  = qs('#aurore-compare-block', section);
    var savingsEl     = qs('#aurore-savings', section);
    var financingEl   = qs('#aurore-financing-price', section);
    var stickyPrice   = qs('#aurore-sticky-price', section);
    var stickyVariant = qs('#aurore-sticky-variant', section);

    /* -- State: current option values by position -- */
    var currentOptions = { 1: null, 2: null, 3: null };

    /* Seed from whichever option button/swatch is active on load */
    qsa('.aurore-variant-group', section).forEach(function (group) {
      var pos = null;
      /* find active swatch */
      var activeSwatch = qs('.aurore-swatch.active', group);
      if (activeSwatch) {
        pos = parseInt(activeSwatch.dataset.optionPosition, 10);
        currentOptions[pos] = activeSwatch.dataset.optionValue;
      }
      /* find active size btn */
      var activeSize = qs('.aurore-size-btn.active', group);
      if (activeSize) {
        pos = parseInt(activeSize.dataset.optionPosition, 10);
        currentOptions[pos] = activeSize.dataset.optionValue;
      }
      /* find selected gem option */
      var selectedGem = qs('.aurore-gem-option.selected', group);
      if (selectedGem) {
        pos = parseInt(selectedGem.dataset.optionPosition, 10);
        currentOptions[pos] = selectedGem.dataset.optionValue;
      }
      var selectedOption = qs('.aurore-option-select', group);
      if (selectedOption) {
        pos = parseInt(selectedOption.dataset.optionPosition, 10);
        currentOptions[pos] = selectedOption.value;
      }
    });

    /* -- Fetch matching variant via Section Rendering API (safe for >250 variants) -- */
    var _fetchDebounceTimer = null;

    function fetchVariantByOptionValues() {
      clearTimeout(_fetchDebounceTimer);
      _fetchDebounceTimer = setTimeout(_doFetchVariant, 80);
    }

    function _doFetchVariant() {
      var productHandle = section.dataset.productHandle;
      var sectionId = section.dataset.sectionId;
      if (!productHandle || !sectionId || !variantIdInput) return;

      var valueIds = [];
      qsa('.aurore-variant-group', section).forEach(function (group) {
        var el = qs('.aurore-swatch.active', group) ||
                 qs('.aurore-size-btn.active', group) ||
                 qs('.aurore-gem-option.selected', group);
        if (!el) {
          var selectEl = qs('.aurore-option-select', group);
          if (selectEl && selectEl.selectedIndex >= 0) {
            el = selectEl.options[selectEl.selectedIndex];
            if (el) {
              el.dataset.optionValue = selectEl.value;
              el.dataset.optionPosition = selectEl.dataset.optionPosition;
            }
          }
        }
        if (el && el.dataset.optionValueId) {
          valueIds.push(el.dataset.optionValueId);

          /* Combined listing: redirect if option maps to a different product */
          var pUrl = el.dataset.productUrl;
          if (pUrl && pUrl.indexOf('/' + productHandle) === -1 && pUrl !== '' && pUrl.indexOf('/products/') !== -1) {
            window.location.href = pUrl;
            return;
          }
        }
      });

      if (!valueIds.length) return;

      var url = '/products/' + productHandle + '?section_id=' + sectionId + '&option_values=' + valueIds.join(',');

      fetch(url)
        .then(function (r) {
          if (!r.ok) throw new Error('section-render ' + r.status);
          return r.text();
        })
        .then(function (html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          var newInput = doc.querySelector('.aurore-variant-id-input');
          if (!newInput) return;

          var newVariantId = newInput.value;
          var available = newInput.dataset.available !== 'false';

          variantIdInput.value = newVariantId;

          updatePriceUI({
            priceCents:          newInput.dataset.priceCents,
            compareCents:        newInput.dataset.compareCents,
            autoDiscountPercent: newInput.dataset.autoDiscountPercent,
            variantTitle:        newInput.dataset.variantTitle
          }, available);
        })
        .catch(function () {
          /* silently ignore network errors — variant UI stays at last good state */
        });
    }

    function updatePriceUI(data, available) {
      var originalCents = parseInt(data.priceCents || '0', 10);
      var compareCents = parseInt(data.compareCents || '0', 10);
      var discountPercent = Number(window.auroreStorefrontSalePercent) || parseFloat(data.autoDiscountPercent || '0');
      var displayCents = originalCents;
      var discountSavings = 0;

      if (originalCents > 0 && discountPercent > 0) {
        discountSavings = Math.round(originalCents * (discountPercent / 100));
        displayCents = Math.max(originalCents - discountSavings, 0);
      }

      var displayPrice = displayCents > 0 ? formatPdpMoney(displayCents) : '';

      if (priceEl && displayPrice) priceEl.textContent = displayPrice;
      if (stickyPrice && displayPrice) stickyPrice.textContent = displayPrice;
      if (stickyVariant) {
        var vTitle = (data.variantTitle || '').trim();
        stickyVariant.textContent = (vTitle === 'Default Title' || vTitle === 'Default') ? '' : vTitle;
      }
      if (financingEl && displayCents > 0) financingEl.textContent = formatPdpMoney(Math.round(displayCents / 4)) + '/mo';

      if (discountBlock && wasEl) {
        if (discountSavings > 0) {
          wasEl.textContent = formatPdpMoney(originalCents);
          discountBlock.style.display = 'flex';
        } else {
          discountBlock.style.display = 'none';
        }
      }

      if (compareEl && savingsEl && compareBlock) {
        if (compareCents > displayCents && compareCents > 0) {
          compareEl.textContent = 'Est. retail ' + formatPdpMoney(compareCents);
          savingsEl.textContent = '— you save ' + formatPdpMoney(compareCents - displayCents) + ' total';
          compareEl.style.display = '';
          compareBlock.style.display = 'flex';
        } else if (discountSavings > 0) {
          compareEl.textContent = '';
          compareEl.style.display = 'none';
          savingsEl.textContent = 'You save ' + formatPdpMoney(discountSavings) + ' (' + Math.round(discountPercent) + '%)';
          compareBlock.style.display = 'flex';
        } else {
          compareBlock.style.display = 'none';
        }
      }

      if (atcBtn) {
        if (available === false) {
          atcBtn.textContent = 'Sold Out';
          atcBtn.disabled = true;
        } else {
          atcBtn.textContent = 'Add to Cart';
          atcBtn.disabled = false;
        }
      }
    }

    /* -- METAL SWATCHES -- */
    qsa('.aurore-variant-group', section).forEach(function (group) {
      var swatches = qsa('.aurore-swatch', group);
      if (!swatches.length) return;

      var pos = swatches[0] ? parseInt(swatches[0].dataset.optionPosition, 10) : 1;
      var metalLabel = qs('.aurore-metal-label', group);

      swatches.forEach(function (sw) {
        on(sw, 'click', function () {
          swatches.forEach(function (s) {
            s.classList.remove('active');
            s.setAttribute('aria-checked', 'false');
          });
          sw.classList.add('active');
          sw.setAttribute('aria-checked', 'true');
          currentOptions[pos] = sw.dataset.optionValue;
          if (metalLabel) metalLabel.textContent = sw.dataset.optionValue || sw.title;
          fetchVariantByOptionValues();
        });
        on(sw, 'keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); sw.click(); }
        });
      });
    });

    /* -- SIZE / LENGTH BUTTONS -- */
    qsa('.aurore-variant-group', section).forEach(function (group) {
      var btns = qsa('.aurore-size-btn', group);
      if (!btns.length) return;

      btns.forEach(function (btn) {
        var pos = parseInt(btn.dataset.optionPosition, 10);
        on(btn, 'click', function () {
          if (btn.disabled) return;
          btns.forEach(function (b) { b.classList.remove('active'); });
          btn.classList.add('active');
          currentOptions[pos] = btn.dataset.optionValue;

          /* Update length label */
          var lengthLabel = qs('.aurore-length-label', group);
          if (lengthLabel) lengthLabel.textContent = btn.dataset.optionValue;

          fetchVariantByOptionValues();
        });
      });
    });

    /* -- LONG SIZE / LENGTH DROPDOWNS -- */
    qsa('.aurore-option-select', section).forEach(function (select) {
      var pos = parseInt(select.dataset.optionPosition, 10);
      on(select, 'change', function () {
        currentOptions[pos] = select.value;

        var group = select.closest('.aurore-variant-group');
        var lengthLabel = group ? qs('.aurore-length-label', group) : null;
        if (lengthLabel) lengthLabel.textContent = select.value;

        fetchVariantByOptionValues();
      });
    });

    /* -- GEM DROPDOWN -- */
    qsa('.aurore-variant-group', section).forEach(function (group) {
      var trigger = qs('.aurore-gem-trigger', group);
      var list    = qs('.aurore-gem-list', group);
      if (!trigger || !list) return;

      var selectedSpan = qs('#aurore-gem-selected', group) || qs('span', trigger);
      var gemLabel     = qs('.aurore-gem-label', group);

      on(trigger, 'click', function (e) {
        e.stopPropagation();
        var open = list.classList.toggle('open');
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      });

      qsa('.aurore-gem-option', list).forEach(function (opt) {
        on(opt, 'click', function () {
          qsa('.aurore-gem-option', list).forEach(function (o) { o.classList.remove('selected'); });
          opt.classList.add('selected');
          var val = opt.dataset.optionValue;
          var text = opt.textContent.replace('✓', '').trim();
          if (selectedSpan) selectedSpan.textContent = text;
          if (gemLabel) gemLabel.textContent = text;

          var pos = parseInt(opt.dataset.optionPosition, 10);
          currentOptions[pos] = val || text;

          list.classList.remove('open');
          trigger.setAttribute('aria-expanded', 'false');
          fetchVariantByOptionValues();
        });
      });

      /* Close on outside click */
      document.addEventListener('click', function (e) {
        if (!group.contains(e.target)) {
          list.classList.remove('open');
          trigger.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }

  /* ─────────────────────────────────────────
     7. ACCORDION
  ───────────────────────────────────────── */
  function initAccordions() {
    qsa('.aurore-accordion').forEach(function (item) {
      var btn  = qs('.aurore-accordion-trigger', item);
      var body = qs('.aurore-accordion-body', item);
      if (!btn) return;

      on(btn, 'click', function () {
        var isOpen = item.classList.contains('open');
        /* close siblings */
        var parent = item.parentElement;
        qsa('.aurore-accordion', parent).forEach(function (sib) {
          sib.classList.remove('open');
          var sibBtn = qs('.aurore-accordion-trigger', sib);
          if (sibBtn) sibBtn.setAttribute('aria-expanded', 'false');
        });
        /* toggle self */
        if (!isOpen) {
          item.classList.add('open');
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  /* ─────────────────────────────────────────
     8. SIZE GUIDE MODAL
  ───────────────────────────────────────── */
  function initSizeGuide() {
    var overlay = qs('#aurore-size-guide');
    if (!overlay) return;

    function openGuide() {
      overlay.classList.add('open');
      overlay.setAttribute('aria-hidden', 'false');
      lockBody();
      /* focus close button */
      var closeBtn = qs('.aurore-sg-close', overlay);
      if (closeBtn) setTimeout(function () { closeBtn.focus(); }, 80);
    }

    function closeGuide() {
      overlay.classList.remove('open');
      overlay.setAttribute('aria-hidden', 'true');
      unlockBody();
    }

    /* Open triggers */
    qsa('.aurore-size-guide-btn').forEach(function (btn) {
      on(btn, 'click', openGuide);
    });

    /* Close: X button */
    on(qs('.aurore-sg-close', overlay), 'click', closeGuide);

    /* Close: click outside panel */
    on(overlay, 'click', function (e) {
      if (e.target === overlay) closeGuide();
    });

    /* Tab switching */
    var tabs     = qsa('.aurore-sg-tab', overlay);
    var contents = qsa('.aurore-sg-content', overlay);

    tabs.forEach(function (tab) {
      on(tab, 'click', function () {
        var target = tab.dataset.tab;
        tabs.forEach(function (t) {
          t.classList.toggle('active', t === tab);
          t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
        });
        contents.forEach(function (c) {
          c.classList.toggle('active', c.dataset.tab === target);
        });
      });
    });
  }

  /* ─────────────────────────────────────────
     9–10. CART DRAWER + SHIPPING BAR
  ───────────────────────────────────────── */

  /* Mini cart state (Shopify Ajax API integration pattern) */
  var cartState = { items: [], item_count: 0, total_price: 0 };

  function initCartDrawer() {
    var drawer  = qs('#aurore-cart-drawer');
    var overlay = qs('#aurore-cart-overlay');

    /* ---- Open / Close ---- */
    function openDrawer() {
      if (!drawer) {
        if (typeof window.aurOpenCart === 'function') window.aurOpenCart();
        return;
      }
      drawer.classList.add('open');
      drawer.setAttribute('aria-hidden', 'false');
      if (overlay) { overlay.classList.add('open'); overlay.setAttribute('aria-hidden', 'false'); }
      lockBody();
      fetchCart();
    }

    function closeDrawer() {
      if (!drawer) return;
      drawer.classList.remove('open');
      drawer.setAttribute('aria-hidden', 'true');
      if (overlay) { overlay.classList.remove('open'); overlay.setAttribute('aria-hidden', 'true'); }
      unlockBody();
    }

    if (drawer) {
      /* Open triggers: legacy PDP drawer support only. The header drawer uses .aur-open-cart. */
      qsa('.aurore-open-cart').forEach(function (btn) {
        on(btn, 'click', function (e) { e.preventDefault(); openDrawer(); });
      });

      /* Close triggers */
      on(qs('.aurore-cd-close', drawer), 'click', closeDrawer);
      on(overlay, 'click', closeDrawer);
      on(qs('.aurore-cd-empty-btn', drawer), 'click', closeDrawer);
    }

    /* ---- ATC button on PDP ---- */
    var atcBtn = qs('.aurore-atc-btn');
    var stickyAddBtn = qs('.aurore-sticky-add');

    function cleanPropertyName(name) {
      return String(name || '').replace(/^properties\[/, '').replace(/\]$/, '').trim();
    }

    function normalizePropertyName(name) {
      return cleanPropertyName(name).replace(/^_+/, '').replace(/_/g, ' ').trim();
    }

    function isPropertyField(el) {
      return el && el.name && /^properties\[[^\]]+\]$/.test(el.name);
    }

    function shouldReadPropertyField(el) {
      if (!isPropertyField(el) || el.disabled) return false;
      var type = (el.type || '').toLowerCase();
      if ((type === 'checkbox' || type === 'radio') && !el.checked) return false;
      return true;
    }

    function getPropertyFieldValue(el) {
      var tag = (el.tagName || '').toLowerCase();
      if (tag === 'select') {
        return el.options && el.selectedIndex >= 0 ? el.options[el.selectedIndex].value : el.value;
      }
      return el.value;
    }

    function getShipsByProperty() {
      var deliveryTitle = qs('[data-delivery-title]');
      if (!deliveryTitle) return '';
      var text = deliveryTitle.textContent || '';
      var match = text.match(/ships by\s+(.+)$/i);
      return match ? match[1].trim() : '';
    }

    function clearHulkValidationErrors() {
      qsa('.aurore-hulk-option-message').forEach(function (message) { message.remove(); });
      qsa('.aurore-hulk-option-error').forEach(function (option) {
        option.classList.remove('aurore-hulk-option-error');
      });
    }

    function addHulkValidationError(option, message) {
      if (!option) return;
      option.classList.add('aurore-hulk-option-error');
      if (!qs('.aurore-hulk-option-message', option)) {
        var error = document.createElement('p');
        error.className = 'aurore-hulk-option-message';
        error.textContent = message || 'Please select an option.';
        option.appendChild(error);
      }
    }

    function formatPdpMoney(cents) {
      var amount = (cents / 100).toFixed(2);
      return '$' + amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    function validateHulkLineItemOptions() {
      var hulkRoot = qs('.hulkapps_product_options');
      if (!hulkRoot) return true;

      clearHulkValidationErrors();
      var valid = true;
      var firstInvalid = null;

      qsa('.hulkapps_option.required', hulkRoot).forEach(function (option) {
        var optionValid = true;
        var hiddenProperty = qs('input[name^="properties["]', option);
        var checkbox = qs('input[type="checkbox"].hulk_po_checkbox, input[type="checkbox"][name^="properties["]', option);
        var select = qs('select[name^="properties["]', option);
        var textInput = qs('input:not([type="hidden"])[name^="properties["], textarea[name^="properties["]', option);

        if (hiddenProperty && !String(hiddenProperty.value || '').trim()) optionValid = false;
        if (checkbox && !checkbox.checked) optionValid = false;
        if (select && !String(select.value || '').trim()) optionValid = false;
        if (textInput && !String(textInput.value || '').trim()) optionValid = false;

        if (!optionValid) {
          valid = false;
          if (!firstInvalid) firstInvalid = option;
          addHulkValidationError(option, checkbox ? 'Please confirm this option.' : 'Please select an option.');
        }
      });

      if (!valid && firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return valid;
    }

    document.addEventListener('click', function (event) {
      if (event.target.closest('.hulkapps_option_child, .hulkapps_swatch_option, .hulkapps_check_option')) {
        setTimeout(clearHulkValidationErrors, 25);
      }
    });

    document.addEventListener('input', function (event) {
      if (event.target.closest('.hulkapps_product_options')) clearHulkValidationErrors();
    });

    function openActiveCartDrawer() {
      if (typeof window.aurOpenCart === 'function') {
        window.aurOpenCart();
        return;
      }
      var headerCartTrigger = qs('.aur-open-cart');
      if (headerCartTrigger) {
        headerCartTrigger.click();
        return;
      }
      if (drawer) openDrawer();
    }

    function getLineItemProperties() {
      var properties = {};

      qsa('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]').forEach(function (field) {
        if (!shouldReadPropertyField(field)) return;
        var key = normalizePropertyName(field.name);
        var value = getPropertyFieldValue(field);
        if (!key || value === null || value === undefined) return;
        value = String(value).trim();
        if (!value) return;
        properties[key] = value;
      });

      var personalization = qs('.aurore-personalization-input');
      if (personalization) {
        var personalizationValue = personalization.value.trim();
        if (personalizationValue) properties.Personalization = personalizationValue;
      }

      var giftToggle = qs('[data-gift-note-toggle]');
      var giftNote = qs('[data-gift-note-input]');
      if (giftToggle && giftToggle.checked && giftNote && giftNote.value.trim()) {
        properties['Gift Note'] = giftNote.value.trim();
      }

      var shipsBy = getShipsByProperty();
      if (shipsBy && !properties['Ships By Date']) {
        properties['Ships By Date'] = shipsBy;
      }

      return Object.keys(properties).length ? properties : null;
    }

    function handleATC(e) {
      e.preventDefault();
      var btn = e.currentTarget;
      var form = qs('#aurore-product-form');
      if (form && !form.reportValidity()) return;
      if (!validateHulkLineItemOptions()) return;

      var variantIdEl = qs('.aurore-variant-id-input');
      var variantId = variantIdEl ? variantIdEl.value : null;
      if (!variantId) return;

      /* Button feedback */
      var origText = btn.textContent;
      btn.textContent = 'Adding…';
      btn.disabled = true;

      addToCart(variantId, 1, getLineItemProperties(), function () {
        btn.textContent = 'Added ✓';
        btn.classList.add('added');
        setTimeout(function () {
          btn.textContent = origText;
          btn.classList.remove('added');
          btn.disabled = false;
        }, 1800);
        openActiveCartDrawer();
        showToast('Added to bag');
      }, function () {
        btn.textContent = origText;
        btn.disabled = false;
        showToast('Something went wrong. Please try again.');
      }, !!qs('#aurore-product-certificate:checked'));
    }

    on(atcBtn, 'click', handleATC);
    on(stickyAddBtn, 'click', function (e) {
      e.preventDefault();
      var form = qs('#aurore-product-form');
      if (form && !form.reportValidity()) return;
      if (!validateHulkLineItemOptions()) return;

      var variantIdEl = qs('.aurore-variant-id-input');
      var variantId = variantIdEl ? variantIdEl.value : null;
      if (!variantId) return;
      addToCart(variantId, 1, getLineItemProperties(), function () {
        openActiveCartDrawer();
        showToast('Added to bag');
      }, function () { showToast('Unable to add items. Please try again.'); }, !!qs('#aurore-product-certificate:checked'));
    });

    /* ---- Delegate: qty, remove, upsell ---- */
    on(drawer, 'click', function (e) {
      /* Qty decrease */
      var qtyMinus = e.target.closest('.aurore-cd-qty-btn[data-action="decrease"]');
      if (qtyMinus) {
        var lineIndex = parseInt(qtyMinus.dataset.line, 10);
        var currentQty = parseInt(qtyMinus.closest('.aurore-cd-item').querySelector('.aurore-cd-qty-num').textContent, 10);
        changeLineQty(lineIndex, Math.max(0, currentQty - 1));
        return;
      }
      /* Qty increase */
      var qtyPlus = e.target.closest('.aurore-cd-qty-btn[data-action="increase"]');
      if (qtyPlus) {
        var lineIndex2 = parseInt(qtyPlus.dataset.line, 10);
        var currentQty2 = parseInt(qtyPlus.closest('.aurore-cd-item').querySelector('.aurore-cd-qty-num').textContent, 10);
        changeLineQty(lineIndex2, currentQty2 + 1);
        return;
      }
      /* Remove */
      var removeBtn = e.target.closest('.aurore-cd-item-remove');
      if (removeBtn) {
        var lineIndex3 = parseInt(removeBtn.dataset.line, 10);
        changeLineQty(lineIndex3, 0);
        return;
      }
      /* Upsell add */
      var upsellAdd = e.target.closest('.aurore-cd-upsell-add');
      if (upsellAdd) {
        var upsellVariantId = upsellAdd.dataset.variantId;
        if (!upsellVariantId) return;
        upsellAdd.textContent = '…';
        upsellAdd.disabled = true;
        addToCart(upsellVariantId, 1, null, function () {
          upsellAdd.textContent = '✓ Added';
          upsellAdd.classList.add('added');
          upsellAdd.disabled = true;
          showToast('Added to bag');
        });
        return;
      }
    });
  }

  /* ---- Shopify Ajax Cart API calls ---- */
  function addToCart(variantId, qty, properties, onSuccess, onError, includeCertificate) {
    var payload = { id: variantId, quantity: qty || 1 };
    if (properties) payload.properties = properties;
    if (window.AuroreCertificate) payload = window.AuroreCertificate.productPayload(payload, includeCertificate);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(payload)
    })
    .then(function (res) {
      if (!res.ok) throw new Error('Add to cart failed');
      return res.json();
    })
    .then(function (data) {
      if (onSuccess) onSuccess(data);
      fetchCart();
      return data;
    })
    .catch(function (err) {
      console.error('[Aurore] addToCart error:', err);
      if (onError) onError(err);
    });
  }

  function changeLineQty(line, qty) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ line: line, quantity: qty })
    })
    .then(function (res) { return res.json(); })
    .then(function (data) { renderCart(data); })
    .catch(function (err) { console.error('[Aurore] changeLineQty error:', err); });
  }

  function fetchCart(callback) {
    return fetch('/cart.js', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
    .then(function (res) { return res.json(); })
    .then(function (data) {
      cartState = data;
      renderCart(data);
      if (callback) callback(data);
      return data;
    })
    .catch(function (err) {
      console.error('[Aurore] fetchCart error:', err);
      if (callback) callback(cartState);
      return cartState;
    });
  }

  /* ---- Render Cart Drawer ---- */
  function renderCart(data) {
    var drawer = qs('#aurore-cart-drawer');
    if (!drawer) return;

    var items     = data.items || [];
    var itemCount = data.item_count || 0;
    var total     = data.total_price || 0; /* cents */
    var totalDollars = (total / 100).toFixed(2);

    /* Item count (header + nav badge) */
    var countEl = qs('.aurore-cd-item-count', drawer);
    if (countEl) countEl.textContent = itemCount + ' ' + (itemCount === 1 ? 'item' : 'items');

    qsa('.aurore-cart-badge').forEach(function (badge) { badge.textContent = itemCount; });
    qsa('[data-aurore-cart-count]').forEach(function (el) { el.textContent = itemCount; });

    /* Subtotal */
    var subEl = qs('.aurore-cd-subtotal-amount', drawer);
    if (subEl) subEl.textContent = formatMoney(total);

    var checkoutBtn = qs('.aurore-cd-checkout-btn', drawer);
    if (checkoutBtn) checkoutBtn.textContent = 'Checkout — ' + formatMoney(total);

    /* Shipping bar */
    updateShippingBar(total / 100);

    /* Toggle empty / filled views */
    var emptyEl  = qs('#aurore-cd-empty', drawer);
    var itemsEl  = qs('#aurore-cd-items', drawer);
    var upsellEl = qs('#aurore-cd-upsell', drawer);
    var footerEl = qs('#aurore-cd-footer', drawer);

    if (items.length === 0) {
      if (emptyEl)  emptyEl.style.display  = 'flex';
      if (itemsEl)  itemsEl.innerHTML = '';
      if (upsellEl) upsellEl.style.display = 'none';
      if (footerEl) footerEl.style.display = 'none';
    } else {
      if (emptyEl)  emptyEl.style.display  = 'none';
      if (upsellEl) upsellEl.style.display = 'block';
      if (footerEl) footerEl.style.display = 'block';

      if (itemsEl) {
        itemsEl.innerHTML = items.map(function (item, idx) {
          var lineNum   = idx + 1;
          var lineTotal = formatMoney(item.line_price);
          var imgSrc    = item.image ? item.image : '';
          var variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
          var propertiesHtml = '';

          if (item.properties) {
            Object.keys(item.properties).forEach(function (key) {
              var value = item.properties[key];
              if (!value) return;
              var label = normalizePropertyName(key);
              if (!label) return;
              if (label.toLowerCase() === 'time notification') return;
              if (key.charAt(0) === '_') {
                var labelLower = label.toLowerCase();
                var isShippingMeta = labelLower.indexOf('ships by') !== -1
                  || labelLower.indexOf('ship by') !== -1
                  || labelLower.indexOf('shipping date') !== -1
                  || labelLower.indexOf('delivery date') !== -1;
                if (!isShippingMeta) return;
              }
              propertiesHtml += '<div class="aurore-cd-item-property"><span class="aurore-cd-item-property-label">' + escHtml(label) + ':</span> ' + escHtml(value) + '</div>';
            });
          }

          return '<div class="aurore-cd-item" data-line="' + lineNum + '">'
            + '<div class="aurore-cd-item-img">'
            + (imgSrc ? '<img src="' + imgSrc + '" alt="' + escHtml(item.title) + '" width="76" height="76" loading="lazy">' : '')
            + '</div>'
            + '<div>'
            + '<div class="aurore-cd-item-name">' + escHtml(item.product_title) + '</div>'
            + (variantTitle ? '<div class="aurore-cd-item-variant">' + escHtml(variantTitle) + '</div>' : '')
            + propertiesHtml
            + '<div class="aurore-cd-item-bottom">'
            + '<div class="aurore-cd-item-price">' + lineTotal + '</div>'
            + '<div class="aurore-cd-item-qty">'
            + '<button class="aurore-cd-qty-btn" data-action="decrease" data-line="' + lineNum + '" aria-label="Decrease quantity">−</button>'
            + '<span class="aurore-cd-qty-num">' + item.quantity + '</span>'
            + '<button class="aurore-cd-qty-btn" data-action="increase" data-line="' + lineNum + '" aria-label="Increase quantity">+</button>'
            + '</div>'
            + '</div>'
            + '<button class="aurore-cd-item-remove" data-line="' + lineNum + '">Remove</button>'
            + '</div>'
            + '</div>';
        }).join('');
      }
    }
  }

  /* ---- Shipping Progress Bar ---- */
  function updateShippingBar(totalDollars) {
    var drawer = qs('#aurore-cart-drawer');
    if (!drawer) return;
    var fill    = qs('.aurore-cd-shipping-fill', drawer);
    var textEl  = qs('.aurore-cd-shipping-text', drawer);
    var pct     = Math.min((totalDollars / FREE_SHIPPING_THRESHOLD) * 100, 100);
    var remaining = Math.max(FREE_SHIPPING_THRESHOLD - totalDollars, 0);

    if (fill) fill.style.width = pct + '%';
    if (textEl) {
      if (totalDollars >= FREE_SHIPPING_THRESHOLD) {
        textEl.innerHTML = '&#127881; <strong>Free shipping unlocked!</strong>';
      } else {
        textEl.innerHTML = 'Add <strong>$' + remaining.toFixed(0) + '</strong> more for free shipping';
      }
    }
  }

  /* ─────────────────────────────────────────
     11. TOAST
  ───────────────────────────────────────── */
  function showToast(msg) {
    var existing = qs('.aurore-toast');
    if (existing) existing.remove();

    var toast = document.createElement('div');
    toast.className = 'aurore-toast';
    toast.innerHTML = '<span>&#10003;</span><span>' + escHtml(msg) + '</span>';
    document.body.appendChild(toast);

    requestAnimationFrame(function () {
      requestAnimationFrame(function () { toast.classList.add('show'); });
    });

    setTimeout(function () {
      toast.classList.remove('show');
      setTimeout(function () { toast.remove(); }, 300);
    }, 3000);
  }

  /* ─────────────────────────────────────────
     12. STICKY ATC
  ───────────────────────────────────────── */
  function initStickyATC() {
    var stickyBar = qs('#aurore-sticky-atc');
    var atcBtn    = qs('.aurore-atc-btn');
    if (!stickyBar || !atcBtn || !('IntersectionObserver' in window)) return;

    new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        stickyBar.classList.toggle('visible', scrolledPast);
        stickyBar.setAttribute('aria-hidden', scrolledPast ? 'false' : 'true');
      });
    }, { threshold: 0 }).observe(atcBtn);
  }

  /* ─────────────────────────────────────────
     13. RECENTLY VIEWED (localStorage)
  ───────────────────────────────────────── */
  function initRecentlyViewed() {
    try {
      var key     = 'aurore_recently_viewed';
      var current = window.__aurore_product_handle;   /* set in liquid via script tag below */
      var stored  = JSON.parse(localStorage.getItem(key) || '[]');
      var section = qs('[data-aurore-recently-section]');
      var grid = qs('#aurore-recently-viewed');
      if (!Array.isArray(stored)) stored = [];

      if (current) {
        /* Remove current product and re-add at front */
        stored = stored.filter(function (p) { return p.handle !== current; });
        stored.unshift({
          handle: window.__aurore_product_handle,
          title:  window.__aurore_product_title,
          url:    window.__aurore_product_url,
          price:  window.__aurore_product_price,
          image:  window.__aurore_product_image,
          cardHtml: window.__aurore_product_card_html
        });
        if (stored.length > 8) stored = stored.slice(0, 8);
        localStorage.setItem(key, JSON.stringify(stored));
      }

      /* Render the grid (skip current product) */
      if (!grid || !section) return;

      var toShow = stored.filter(function (p) {
        return p && p.handle && p.handle !== current && p.cardHtml;
      }).slice(0, 8);
      if (toShow.length === 0) {
        grid.innerHTML = '';
        section.hidden = true;
        return;
      }

      grid.innerHTML = toShow.map(function (p) { return p.cardHtml; }).join('');
      section.hidden = false;

    } catch (e) {
      /* localStorage may be unavailable */
    }
  }

  function openShopifyInboxChat() {
    var apiCandidates = [
      window.ShopifyInbox,
      window.ShopifyInboxWidget,
      window.Shopify && window.Shopify.inbox,
      window.Shopify && window.Shopify.chat,
      window.ShopifyChat
    ].filter(Boolean);

    for (var i = 0; i < apiCandidates.length; i += 1) {
      var api = apiCandidates[i];
      var methods = ['open', 'show', 'toggle', 'openChat', 'showChat'];
      for (var j = 0; j < methods.length; j += 1) {
        if (typeof api[methods[j]] === 'function') {
          api[methods[j]]();
          return true;
        }
      }
    }

    var eventNames = [
      'shopify:inbox:open',
      'shopify:chat:open',
      'ShopifyInbox:open'
    ];
    eventNames.forEach(function(eventName) {
      document.dispatchEvent(new CustomEvent(eventName, { bubbles: true }));
      window.dispatchEvent(new CustomEvent(eventName));
    });

    var launcherSelectors = [
      'shopify-inbox',
      'shopify-chat',
      '#ShopifyChat',
      '#shopify-chat button',
      '#shopify-chat',
      '[id*="ShopifyChat"] button',
      '[id*="shopify-chat"] button',
      '[class*="shopify-chat"] button',
      '[class*="ShopifyChat"] button',
      '[data-testid*="chat" i]',
      '[data-test-id*="chat" i]',
      '[aria-label*="chat" i]',
      '[title*="chat" i]',
      '[role="button"][aria-label*="message" i]',
      'iframe[title*="chat" i]',
      'iframe[title*="message" i]',
      'iframe[src*="chat"]',
      'iframe[src*="inbox"]'
    ];

    function findLauncherIn(root) {
      for (var index = 0; index < launcherSelectors.length; index += 1) {
        var node = root.querySelector && root.querySelector(launcherSelectors[index]);
        if (node) return node;
      }

      var nodes = root.querySelectorAll ? qsa('*', root) : [];
      for (var indexNode = 0; indexNode < nodes.length; indexNode += 1) {
        if (nodes[indexNode].shadowRoot) {
          var shadowNode = findLauncherIn(nodes[indexNode].shadowRoot);
          if (shadowNode) return shadowNode;
        }
      }

      return null;
    }

    var shadowLauncher = findLauncherIn(document);
    if (shadowLauncher && typeof shadowLauncher.click === 'function') {
      shadowLauncher.click();
      return true;
    }

    for (var k = 0; k < launcherSelectors.length; k += 1) {
      var launcher = qs(launcherSelectors[k]);
      if (launcher && typeof launcher.click === 'function') {
        launcher.click();
        return true;
      }
    }

    return false;
  }

  function initCustomOrderInfo() {
    var coToggle = qs('[data-aurore-co-toggle]');
    var coFormWrap = coToggle && qs('#' + (coToggle.getAttribute('aria-controls') || 'aurore-co-form-wrap'));
    if (coToggle && coFormWrap) {
      on(coToggle, 'click', function() {
        var isOpen = coToggle.getAttribute('aria-expanded') === 'true';
        var opening = !isOpen;
        coToggle.setAttribute('aria-expanded', String(opening));
        coFormWrap.classList.toggle('is-open', opening);
        if (opening) {
          window.setTimeout(function() {
            coFormWrap.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }, 50);
        }
      });
    }
  }

  /* ─────────────────────────────────────────
     14. ESCAPE KEY + BODY LOCK
  ───────────────────────────────────────── */
  function lockBody() {
    document.body.classList.add('aurore-locked');
  }
  function unlockBody() {
    /* Only unlock if neither drawer nor size guide is open */
    var cartOpen = qs('#aurore-cart-drawer.open');
    var sgOpen   = qs('#aurore-size-guide.open');
    if (!cartOpen && !sgOpen) document.body.classList.remove('aurore-locked');
  }

  document.addEventListener('keyup', function (e) {
    if (e.key !== 'Escape') return;
    /* Close size guide */
    var sg = qs('#aurore-size-guide');
    if (sg && sg.classList.contains('open')) {
      sg.classList.remove('open');
      sg.setAttribute('aria-hidden', 'true');
      unlockBody();
    }
    /* Close cart drawer */
    var cd = qs('#aurore-cart-drawer');
    var co = qs('#aurore-cart-overlay');
    if (cd && cd.classList.contains('open')) {
      cd.classList.remove('open');
      cd.setAttribute('aria-hidden', 'true');
      if (co) { co.classList.remove('open'); co.setAttribute('aria-hidden', 'true'); }
      unlockBody();
    }
  });

  /* ─────────────────────────────────────────
     UTILITIES
  ───────────────────────────────────────── */

  /* Format Shopify money (cents) — full two-decimal with comma thousands, used by PDP price UI */
  function formatPdpMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    return '$' + amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* Format Shopify money (cents) */
  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    /* Strip trailing .00 for cleaner display */
    if (amount.endsWith('.00')) amount = amount.slice(0, -3);
    return '$' + amount;
  }

  /* HTML escape */
  function escHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatPdpReviewBadge() {
    var badge = qs('.aurore-rating .jdgm-prev-badge');
    if (!badge) return false;

    var count = 0;
    var countSources = [
      badge.getAttribute('data-number-of-reviews'),
      badge.dataset.numberOfReviews,
      badge.getAttribute('data-number-of-questions'),
      badge.querySelector('.jdgm-prev-badge__text') && badge.querySelector('.jdgm-prev-badge__text').textContent,
      badge.querySelector('.jdgm-prev-badge__count') && badge.querySelector('.jdgm-prev-badge__count').textContent,
      badge.querySelector('.jdgm-numreviews') && badge.querySelector('.jdgm-numreviews').textContent,
      badge.textContent
    ];

    countSources.some(function(source) {
      var match = String(source || '').match(/\d+/);
      if (!match) return false;
      count = parseInt(match[0], 10) || 0;
      return count > 0;
    });

    var ratingWrap = badge.closest('.aurore-rating');
    if (count <= 0 || /no\s+reviews/i.test(badge.textContent || '')) {
      if (ratingWrap) ratingWrap.hidden = true;
      return true;
    }

    if (ratingWrap) ratingWrap.hidden = false;
    var label = count === 1 ? '1 Review' : count + ' Reviews';
    var target = badge.querySelector('.jdgm-prev-badge__text, .jdgm-prev-badge__count, .jdgm-numreviews');
    if (!target) {
      target = document.createElement('span');
      target.className = 'jdgm-prev-badge__text';
      badge.appendChild(target);
    }
    target.textContent = label;
    qsa('.jdgm-prev-badge__count, .jdgm-numreviews', badge).forEach(function(node) {
      if (node !== target) node.remove();
    });
    return true;
  }

  function initPdpReviewBadge() {
    var attempts = 0;
    var timer = window.setInterval(function() {
      attempts += 1;
      formatPdpReviewBadge();
      if (attempts >= 24) {
        window.clearInterval(timer);
      }
    }, 250);
  }

  function initPdpReviews() {
    var section = qs('[data-aurore-pdp-reviews]');
    if (!section) return;
    if (section.dataset.aurorePdpReviewsReady === 'true') return;
    section.dataset.aurorePdpReviewsReady = 'true';
    var reviewDataCache = null;
    var reviewsActivated = false;
    var reviewSyncScheduled = false;
    var reviewObserver = null;
    var widgetAttempts = 0;
    var widgetTimer = null;
    function getProductWidget() {
      var widgets = qsa('#judgeme_product_reviews, .jdgm-review-widget', document);
      return widgets.find(function(widget) {
        return widget.innerHTML &&
          widget.innerHTML.replace(/\s+/g, '').length > 100;
      }) || null;
    }

    function moveJudgeMeProductWidget() {
      var slot = qs('[data-aurore-judge-me-slot]', section);
      var widget = getProductWidget();
      if (!slot || !widget) return false;

      var source = widget.closest('.shopify-app-block') || widget;
      if (slot.contains(source)) return true;

      source.classList.add('aurore-pdp-native-review-block');
      slot.appendChild(source);
      slot.hidden = false;
      syncPdpReviewDisplay();
      return true;
    }

    function escapeReviewHtml(value) {
      return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    function readStoreReviewData() {
      if (reviewDataCache) return reviewDataCache;
      var script = qs('[data-aurore-store-reviews]', section);
      if (!script || !script.textContent) return null;
      try {
        reviewDataCache = JSON.parse(script.textContent);
        return reviewDataCache;
      } catch (error) {
        return null;
      }
    }

    function normalizeReviewPhotos(photos) {
      if (!photos) return [];
      if (Array.isArray(photos)) {
        return photos.map(function(photo) {
          if (typeof photo === 'string') return photo;
          return photo && (
            photo.url ||
            photo.src ||
            photo.href ||
            photo.compact ||
            photo.small ||
            photo.thumbnail_url ||
            photo.original ||
            photo.original_url ||
            photo.huge
          );
        }).filter(Boolean);
      }
      if (typeof photos === 'object') {
        return Object.keys(photos).map(function(key) {
          var photo = photos[key];
          if (typeof photo === 'string') return photo;
          return photo && (
            photo.url ||
            photo.src ||
            photo.href ||
            photo.compact ||
            photo.small ||
            photo.thumbnail_url ||
            photo.original ||
            photo.original_url ||
            photo.huge
          );
        }).filter(Boolean);
      }
      if (typeof photos === 'string') {
        try {
          return normalizeReviewPhotos(JSON.parse(photos));
        } catch (error) {
          return photos.split(',').map(function(photo) { return photo.trim(); }).filter(Boolean);
        }
      }
      return [];
    }

    function renderReviewStars(rating) {
      var score = Math.max(1, Math.min(5, parseInt(rating, 10) || 5));
      var html = '';
      for (var i = 1; i <= 5; i += 1) {
        html += i <= score ? '★' : '<span>★</span>';
      }
      return html;
    }

    function readReviewNodeText(node, selector) {
      var target = qs(selector, node);
      return target ? target.textContent.replace(/\s+/g, ' ').trim() : '';
    }

    function getProductReviewRating(review) {
      var ratingNode = qs('[aria-label*="star" i], [aria-label*="rating" i], .jdgm-rev__rating, .jm-star-rating', review);
      var label = ratingNode && (ratingNode.getAttribute('aria-label') || ratingNode.textContent || '');
      var match = label && label.match(/[1-5](?:\.\d+)?/);
      if (match) return Math.round(parseFloat(match[0]));
      var activeStars = qsa('.jdgm--on, .jdgm--half, [class*="filled" i], [class*="active" i]', review).length;
      return activeStars ? Math.max(1, Math.min(5, activeStars)) : 5;
    }

    function normalizeReviewKey(review) {
      return (review && review.body || '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 160);
    }

    function readReviewImageUrl(img) {
      if (!img) return '';
      var src = img.getAttribute('src') ||
        img.getAttribute('data-src') ||
        img.getAttribute('data-original') ||
        img.getAttribute('data-lazy-src') ||
        '';
      if (src) return src;

      var srcset = img.getAttribute('srcset') || img.getAttribute('data-srcset') || '';
      if (!srcset) return '';
      var candidate = srcset.split(',').map(function(item) {
        return item.trim().split(/\s+/)[0];
      }).filter(Boolean).pop();
      return candidate || '';
    }

    function getProductReviewPhotos(review) {
      var selectors = [
        '.jm-media-thumbnail-list__thumbnail img',
        '.jm-review-media img',
        '.jm-review__media img',
        '.jdgm-rev__pics img',
        '.jdgm-rev__pic img',
        '.jdgm-rev__media img',
        '.jdgm-rev__pic-link img',
        '.jdgm-rev__pic-img',
        '.jdgm-rev__photo img',
        '.jdgm-rev__image img',
        '.jdgm-rev__media-item img',
        '[class*="media"] img',
        '[class*="photo"] img',
        '[class*="pic"] img'
      ].join(', ');

      return qsa(selectors, review).map(readReviewImageUrl).filter(function(url, index, urls) {
        return url && !/^data:/i.test(url) && urls.indexOf(url) === index;
      });
    }

    function enrichProductReviews(productReviews, storeReviews) {
      if (!productReviews.length || !storeReviews.length) return productReviews;
      var storeByBody = new Map();
      storeReviews.forEach(function(review) {
        var key = normalizeReviewKey(review);
        if (key && !storeByBody.has(key)) storeByBody.set(key, review);
      });

      return productReviews.map(function(review) {
        var match = storeByBody.get(normalizeReviewKey(review));
        if (!match) return review;
        var merged = Object.assign({}, review);
        if (!merged.product && match.product) merged.product = match.product;
        if ((!merged.photos || !merged.photos.length) && match.photos) merged.photos = match.photos;
        if (!merged.title && match.title) merged.title = match.title;
        if (!merged.date && match.date) merged.date = match.date;
        return merged;
      });
    }

    function getProductReviewsFromWidget(widget) {
      if (!widget) return [];
      return qsa('.jm-review-item, .jdgm-rev', widget).map(function(review) {
        var body = readReviewNodeText(review, '.jdgm-review-content__body-content, .jm-review-content__body, .jdgm-rev__body');
        if (!body) return null;
        var author = readReviewNodeText(review, '.jm-reviewer-info__name, .jdgm-rev__author') || 'Verified customer';
        var details = readReviewNodeText(review, '.jm-reviewer-info__details, .jdgm-rev__timestamp');
        var dateMatch = details.match(/\b\d{1,2}\/\d{1,2}\/\d{2,4}\b|(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4}/i);
        return {
          source: 'product',
          title: readReviewNodeText(review, '.jm-review-content__title, .jdgm-rev__title'),
          body: body,
          author: author,
          product: '',
          rating: getProductReviewRating(review),
          dateLabel: dateMatch ? dateMatch[0] : details.replace(author, '').trim(),
          date: 0,
          photos: getProductReviewPhotos(review)
        };
      }).filter(Boolean);
    }

    function renderFallbackReviewMedia(photos) {
      var urls = normalizeReviewPhotos(photos);
      if (!urls.length) return '';
      var visible = urls.slice(0, 3);
      return '<div class="aurore-pdp-store-review-card__media">' +
        visible.map(function(url, index) {
          var extra = index === 2 && urls.length > 3 ? '<span>+' + (urls.length - 2) + '</span>' : '';
          return '<div><img src="' + escapeReviewHtml(url) + '" alt="" loading="lazy">' + extra + '</div>';
        }).join('') +
      '</div>';
    }

    function renderReviewCard(review) {
      var title = review.title ? '<h3>' + escapeReviewHtml(review.title) + '</h3>' : '';
      var product = review.product ? ' · ' + escapeReviewHtml(review.product) : '';
      return '<article class="aurore-pdp-store-review-card">' +
        '<div class="aurore-pdp-store-review-card__meta">' +
          '<strong>' + escapeReviewHtml(review.author || 'Verified customer') + '</strong>' +
          '<span>' + escapeReviewHtml(review.dateLabel || '') + '</span>' +
        '</div>' +
        '<div class="aurore-pdp-store-review-card__stars" aria-label="' + escapeReviewHtml(review.rating || 5) + ' star review">' + renderReviewStars(review.rating) + '</div>' +
        title +
        '<p>' + escapeReviewHtml(review.body || '') + '</p>' +
        '<div class="aurore-pdp-store-review-card__verified">✓ Verified' + product + '</div>' +
        renderFallbackReviewMedia(review.photos) +
      '</article>';
    }

    function renderReviewDisplay() {
      var target = qs('[data-aurore-store-review-fallback]', section);
      var data = readStoreReviewData();
      if (!target || !data || !Array.isArray(data.reviews) || !data.reviews.length) return false;

      var widget = getProductWidget();
      var storeReviews = data.reviews.slice().sort(function(a, b) {
        return (parseInt(b.date, 10) || 0) - (parseInt(a.date, 10) || 0);
      });
      var productReviews = enrichProductReviews(getProductReviewsFromWidget(widget), storeReviews);
      var productBodies = new Set(productReviews.map(function(review) {
        return normalizeReviewKey(review);
      }));
      var fillReviews = storeReviews.filter(function(review) {
        var key = normalizeReviewKey(review);
        return key && !productBodies.has(key);
      });
      var reviews = productReviews.concat(fillReviews).slice(0, 3);
      if (!reviews.length) return false;

      var histogram = Array.isArray(data.histogram) ? data.histogram : [];
      var maxCount = histogram.reduce(function(max, row) {
        return Math.max(max, parseInt(row.frequency || row.count, 10) || 0);
      }, 0) || 1;

      target.innerHTML = '' +
        '<div class="aurore-pdp-store-review-summary">' +
          '<div class="aurore-pdp-store-review-score">' +
            '<div class="aurore-pdp-store-review-score__value">' + escapeReviewHtml(data.average || '5.0') + '</div>' +
            '<div class="aurore-pdp-store-review-score__stars" aria-hidden="true">★★★★★</div>' +
            '<div class="aurore-pdp-store-review-score__count">' + escapeReviewHtml(data.count || reviews.length) + ' reviews</div>' +
          '</div>' +
          '<div class="aurore-pdp-store-review-bars">' +
            ([5, 4, 3, 2, 1].map(function(rating) {
              var row = histogram.find(function(item) { return parseInt(item.rating, 10) === rating; }) || {};
              var count = parseInt(row.frequency || row.count, 10) || 0;
              var percent = row.percentage != null ? parseFloat(row.percentage) : (count / maxCount * 100);
              return '<div class="aurore-pdp-store-review-bar">' +
                '<span>' + rating + ' ★</span>' +
                '<div><i style="width: ' + Math.max(0, Math.min(100, percent)) + '%;"></i></div>' +
                '<span>' + count + '</span>' +
              '</div>';
            }).join('')) +
          '</div>' +
        '</div>' +
        '<div class="aurore-pdp-store-review-grid">' +
          reviews.map(renderReviewCard).join('') +
        '</div>';

      target.hidden = false;
      return true;
    }

    function widgetHasProductReviews(widget) {
      if (!widget) return false;
      return qsa('.jm-review-item, .jdgm-rev', widget).some(function(review) {
        return (review.textContent || '').replace(/\s+/g, '').length > 20;
      });
    }

    function syncPdpReviewDisplay() {
      var fallback = qs('[data-aurore-store-review-fallback]', section);
      if (!fallback) return;

      if (renderReviewDisplay()) {
        section.classList.add('is-store-review-fallback');
      }
      populateMobileRatingStrip();
    }

    function populateMobileRatingStrip() {
      var strip = qs('.aurore-pdp-mobile-rating-strip', section);
      if (!strip) return;
      var rating = 0, count = 0;
      var widget = getProductWidget();
      if (widget) {
        var wRating = parseFloat(widget.getAttribute('data-average-rating') || widget.getAttribute('data-rating') || '0');
        var wCount  = parseInt(widget.getAttribute('data-number-of-reviews') || widget.getAttribute('data-count') || '0', 10);
        var avgEl = qs('.jdgm-rev-widg__summary-average, .jdgm-average-rating', widget);
        var cntEl = qs('.jdgm-numreviews', widget);
        rating = wRating || (avgEl ? parseFloat(avgEl.textContent) : 0);
        count  = wCount  || (cntEl ? parseInt(cntEl.textContent, 10) : 0);
      }
      if (!rating || !count) {
        var data = readStoreReviewData();
        if (data) {
          rating = rating || parseFloat(data.average) || 0;
          count  = count  || parseInt(data.count, 10) || 0;
        }
      }
      if (!rating && !count) return;
      var starsEl = qs('.aurore-pdp-mrs-stars', strip);
      var countEl = qs('[data-mrs-count]', strip);
      if (starsEl && rating > 0) starsEl.style.setProperty('--r', Math.round(rating / 5 * 100) + '%');
      if (countEl && count > 0) countEl.textContent = count + ' reviews';
    }

    function normalizeProductReviewForm(widget) {
      if (!widget) return;

      var title = qs('.jdgm-form > .jdgm-form__title:first-child, .jdgm-form__title', widget);
      if (title) title.textContent = 'Write a Review';

      var labelMap = [
        ['.jdgm-form__fieldset .jdgm-form__title, .jdgm-form__fieldset > label', ''],
        ['.jdgm-form__fieldset:has(.jdgm-form__rating) .jdgm-form__title, .jdgm-form__fieldset:has(.jdgm-form__rating) > label', 'Rating'],
        ['.jdgm-form__fieldset:has(input[name="review_title"]) .jdgm-form__title, .jdgm-form__fieldset:has(input[name="review_title"]) > label', 'Review Title'],
        ['.jdgm-form__fieldset:has(textarea[name="review_body"]) .jdgm-form__title, .jdgm-form__fieldset:has(textarea[name="review_body"]) > label', 'Review Content'],
        ['.jdgm-form__fieldset:has(input[type="file"]) .jdgm-form__title, .jdgm-form__fieldset:has(input[type="file"]) > label', 'Picture/Video (Optional)'],
        ['.jdgm-form__fieldset:has(input[name="reviewer_name"]) .jdgm-form__title, .jdgm-form__fieldset:has(input[name="reviewer_name"]) > label', 'Display Name'],
        ['.jdgm-form__fieldset:has(input[name="reviewer_email"]) .jdgm-form__title, .jdgm-form__fieldset:has(input[name="reviewer_email"]) > label', 'Email Address']
      ];

      labelMap.forEach(function(pair) {
        if (!pair[1]) return;
        qsa(pair[0], widget).forEach(function(label) {
          label.textContent = pair[1];
        });
      });

      var titleInput = qs('input[name="review_title"]', widget);
      var bodyInput = qs('textarea[name="review_body"]', widget);
      var nameInput = qs('input[name="reviewer_name"]', widget);
      var emailInput = qs('input[name="reviewer_email"]', widget);
      var submit = qs('.jdgm-submit-rev, input[type="submit"], button[type="submit"]', widget);

      if (titleInput) titleInput.setAttribute('placeholder', 'Summarize your experience...');
      if (bodyInput) bodyInput.setAttribute('placeholder', "Tell us about the quality, fit, and how you're wearing it...");
      if (nameInput) nameInput.setAttribute('placeholder', 'First name + last initial');
      if (emailInput) emailInput.setAttribute('placeholder', 'For verification only');
      if (submit) submit.value = submit.textContent = 'Submit Review';

      qsa('input[name="yt_url"]', widget).forEach(function(input) {
        input.setAttribute('data-aurore-yt-url-name', input.name || 'yt_url');
      });
    }

    function scheduleProductReviewFormNormalization(widget) {
      normalizeProductReviewForm(widget);
      window.setTimeout(function() { normalizeProductReviewForm(widget); }, 150);
      window.setTimeout(function() { normalizeProductReviewForm(widget); }, 500);
    }

    function productReviewFormIsVisible() {
      var widget = getProductWidget();
      if (!widget || !widget.classList.contains('is-open')) return false;
      var form = qs('.jdgm-form-wrapper, .jm-form-wrapper', widget);
      if (!form) return false;
      var rect = form.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    }

    function showProductReviewFormMessage() {
      var heading = qs('.aurore-pdp-review-heading', section);
      var target = qs('[data-aurore-pdp-review-message]', section);
      if (!target && heading) {
        target = document.createElement('p');
        target.className = 'aurore-pdp-review-message';
        target.setAttribute('role', 'status');
        target.setAttribute('data-aurore-pdp-review-message', '');
        heading.appendChild(target);
      }
      if (target) {
        target.textContent = 'Review form is loading. Please scroll to the reviews section and try again.';
      } else {
        window.alert('Review form is loading. Please scroll to the reviews section and try again.');
      }
    }

    function setProductReviewModalState(isOpen) {
      document.documentElement.classList.toggle('aurore-pdp-review-modal-open', isOpen);
      document.body.classList.toggle('aurore-pdp-review-modal-open', isOpen);
    }

    var PRODUCT_REVIEW_WRITE_SELECTORS = [
      '.jdgm-rev-widg__summary .jdgm-write-rev-link',
      '.jdgm-write-rev-link.jdgm-write-rev-link',
      '.jdgm-write-rev-link:not(.aurore-pdp-review-write)',
      '.jdgm-write-rev-link--review-widget',
      '.jm-action-buttons__button[data-testid="write-review-button"]',
      '.jm-action-buttons__button',
      '[data-testid="write-review-button"]',
      '[data-write-review]'
    ];

    function getProductReviewTrigger(widget) {
      if (!widget) return null;
      for (var i = 0; i < PRODUCT_REVIEW_WRITE_SELECTORS.length; i += 1) {
        var trigger = qs(PRODUCT_REVIEW_WRITE_SELECTORS[i], widget);
        if (trigger) return trigger;
      }
      return null;
    }

    function prepareProductReviewWidget(widget) {
      if (!widget) return;
      widget.classList.add('is-open');
      widget.removeAttribute('aria-hidden');
      section.classList.add('is-review-form-open');
      setProductReviewModalState(true);
    }

    function openProductReviewFormWithJudgeMeApi(widget) {
      if (!widget || !window.jdgm || !window.jdgm.openForm || !window.jdgm.$) return false;
      setProductReviewModalState(true);
      window.jdgm.openForm(window.jdgm.$(widget));
      window.setTimeout(function() { revealProductReviewModal(widget); }, 100);
      window.setTimeout(function() { revealProductReviewModal(widget); }, 350);
      window.setTimeout(function() { revealProductReviewModal(widget); }, 900);
      scheduleProductReviewFormNormalization(widget);
      return true;
    }

    function revealProductReviewModal(widget) {
      var modalSelectors = [
        '.jdgm-modal',
        '.jm-mfp-wrap',
        '.mfp-wrap',
        '.jdgm-form-wrapper',
        '.jm-form-wrapper',
        '.jdgm-form',
        '.jm-form'
      ];
      var modalNodes = modalSelectors.reduce(function(nodes, selector) {
        return nodes.concat(qsa(selector, document));
      }, []);

      modalNodes.forEach(function(node) {
        node.style.visibility = 'visible';
        node.style.opacity = '1';
        node.style.pointerEvents = 'auto';
      });

      qsa('.jdgm-form-wrapper, .jm-form-wrapper', document).forEach(function(wrapper) {
        wrapper.style.display = 'block';
        wrapper.style.visibility = 'visible';
        wrapper.style.opacity = '1';
        wrapper.style.pointerEvents = 'auto';
      });
    }

    function scrollToProductReviewWidget(widget) {
      var target = widget || section;
      if (!target || !target.scrollIntoView) return;
      try {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (error) {
        target.scrollIntoView();
      }
    }

    function openProductReviewForm() {
      var widget = getProductWidget();
      if (!widget) return false;

      var trigger = getProductReviewTrigger(widget);
      if (trigger) {
        if (trigger.style) trigger.style.display = '';
        trigger.click();
        scheduleProductReviewFormNormalization(widget);
        return true;
      }

      var form = widget && qs('.jdgm-form-wrapper, .jm-form-wrapper', widget);
      if (form) {
        prepareProductReviewWidget(widget);
        form.style.display = 'block';
        scheduleProductReviewFormNormalization(widget);
        return true;
      }

      return false;
    }

    function closeProductReviewForm() {
      var widget = getProductWidget();
      if (widget) {
        widget.classList.remove('is-open');
        widget.setAttribute('aria-hidden', 'true');
      }
      section.classList.remove('is-review-form-open');
      setProductReviewModalState(false);
    }

    on(section, 'click', function(event) {
      var writeButton = event.target.closest('[data-aurore-pdp-write-review]');
      if (!writeButton) return;

      event.preventDefault();
      activatePdpReviews();
      moveJudgeMeProductWidget();

      if (openProductReviewForm()) return;

      var startedAt = Date.now();
      var timer = window.setInterval(function() {
        if (openProductReviewForm()) {
          window.clearInterval(timer);
          return;
        }

        if (Date.now() - startedAt >= 3000) {
          window.clearInterval(timer);
          showProductReviewFormMessage();
        }
      }, 150);
    });

    on(document, 'click', function(event) {
      if (event.target.closest('.jm-mfp-close, .mfp-close, .jdgm-modal__close, .jdgm-close, .jdgm-cancel-rev')) {
        closeProductReviewForm();
      }
    });

    function scheduleReviewSync() {
      if (reviewSyncScheduled) return;
      reviewSyncScheduled = true;
      window.setTimeout(function() {
        syncPdpReviewDisplay();
      }, 1200);
      window.setTimeout(function() {
        syncPdpReviewDisplay();
        reviewSyncScheduled = false;
      }, 2800);
    }

    function startJudgeMeWidgetPolling() {
      if (widgetTimer) return;
      widgetTimer = window.setInterval(function() {
        widgetAttempts += 1;
        if (moveJudgeMeProductWidget() || widgetAttempts > 40) {
          syncPdpReviewDisplay();
          window.clearInterval(widgetTimer);
          widgetTimer = null;
        }
      }, 250);
    }

    function activatePdpReviews() {
      if (reviewsActivated) return;
      reviewsActivated = true;
      section.dataset.aurorePdpReviewsActive = 'true';

      if (reviewObserver) {
        reviewObserver.disconnect();
        reviewObserver = null;
      }

      moveJudgeMeProductWidget();
      syncPdpReviewDisplay();
      scheduleReviewSync();
      startJudgeMeWidgetPolling();
    }

    if (window.location.hash === '#aurore-reviews') {
      activatePdpReviews();
      return;
    }

    if ('IntersectionObserver' in window) {
      reviewObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (!entry.isIntersecting) return;
          activatePdpReviews();
        });
      }, {
        rootMargin: '450px 0px'
      });
      reviewObserver.observe(section);
    } else {
      window.setTimeout(activatePdpReviews, 1200);
    }
  }

  function initPdpReviewsWhenAvailable() {
    initPdpReviews();
  }

  function initPersonalizationCounter() {
    var personalizationInput = qs('.aurore-personalization-input');
    var personalizationCounter = qs('[data-personalization-counter]');
    if (!personalizationInput || !personalizationCounter) return;

    var personalizationLimit = parseInt(
      personalizationInput.getAttribute('data-personalization-maxlength') ||
      personalizationInput.getAttribute('maxlength') ||
      '0',
      10
    );
    if (!personalizationLimit) return;

    function updatePersonalizationCounter() {
      var remainingChars = Math.max(personalizationLimit - personalizationInput.value.length, 0);
      personalizationCounter.textContent = remainingChars + ' Characters';
    }

    personalizationInput.addEventListener('input', updatePersonalizationCounter);
    updatePersonalizationCounter();
  }

  /* ─────────────────────────────────────────
     INIT
  ───────────────────────────────────────── */
  /* Strip outer ring from Hulk select-alphabets swatch wrappers.
     Hulk's inline <style> loads after our CSS and wins the !important
     tie, so we override via inline styles which rank highest. */
  function fixHulkAlphabetCircles() {
    var wrappers = qsa(
      '.hulkapps_option.select-alphabets .hulkapps_swatch_option,' +
      '.hulkapps_option.select-alphabets .hulkapps-tooltip,' +
      '.hulkapps_option.select-alphabets .hulkapps-tooltip > div'
    );
    wrappers.forEach(function (el) {
      el.style.setProperty('border', 'none', 'important');
      el.style.setProperty('box-shadow', 'none', 'important');
      el.style.setProperty('outline', 'none', 'important');
      el.style.setProperty('background', 'transparent', 'important');
      el.style.setProperty('border-radius', '0', 'important');
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initGallery();
    initVariants();
    initAccordions();
    initSizeGuide();
    initCartDrawer();
    initStickyATC();
    initRecentlyViewed();
    initCustomOrderInfo();
    initPdpReviewBadge();
    initEstimatedDelivery();
    initGiftNote();
    initPdpReviewsWhenAvailable();
    initPersonalizationCounter();
    /* Run immediately then again after Hulk finishes rendering */
    fixHulkAlphabetCircles();
    window.setTimeout(fixHulkAlphabetCircles, 400);

    /* Initial cart count on page load */
    fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        qsa('.aurore-cart-badge').forEach(function (b) { b.textContent = data.item_count || 0; });
        qsa('[data-aurore-cart-count]').forEach(function (el) { el.textContent = data.item_count || 0; });
      })
      .catch(function () { /* silently ignore — not in Shopify context */ });
  });

})();
